import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistance } from 'date-fns';
import api from '../../utils/api';
import { AuthContext } from '../../contexts/AuthContext';
import { renderContentWithMentions } from '../../utils/mentions';
import { 
  ClipboardDocumentListIcon, 
  ExclamationCircleIcon, 
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  InboxIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  DocumentIcon,
  BellAlertIcon,
  CalendarIcon,
  PaperClipIcon,
  BookmarkIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

// Utility to check if a date is in the past
const isOverdue = (dateString) => {
  if (!dateString) return false;
  const dueDate = new Date(dateString);
  return dueDate < new Date();
};

// Function to get color for RACI role
const getRoleColor = (role) => {
  switch(role) {
    case 'R': return 'bg-blue-100 text-blue-800';
    case 'A': return 'bg-green-100 text-green-800';
    case 'C': return 'bg-purple-100 text-purple-800';
    case 'I': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Function to get full role name
const getRoleName = (role) => {
  switch(role) {
    case 'R': return 'Responsible';
    case 'A': return 'Accountable';
    case 'C': return 'Consulted';
    case 'I': return 'Informed';
    default: return 'Unknown';
  }
};

// Function to check if task is due soon (within 7 days) 
const isDueSoon = (dateString) => {
  if (!dateString) return false;
  const dueDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 && diffDays <= 7;
};

const MyDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [pendingMissingItems, setPendingMissingItems] = useState([]);
  const [recentlyReceivedItems, setRecentlyReceivedItems] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [clientFiles, setClientFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  
  // Separate time filters for each section
  const [assignmentsTimeFilter, setAssignmentsTimeFilter] = useState('72h');
  const [receivedItemsTimeFilter, setReceivedItemsTimeFilter] = useState('72h');
  const [noteTimeFilter, setNoteTimeFilter] = useState('72h');
  const [clientFilesTimeFilter, setClientFilesTimeFilter] = useState('72h');
  
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  
  // States for expanded sections
  const [expandedSections, setExpandedSections] = useState({
    pendingMissingItems: false,
    recentlyReceivedItems: false,
    recentNotes: false,
    clientFiles: false,
    overdueTasks: false,
    upcomingTasks: false,
    myProjects: false,
    recentAssignments: false
  });
  
  // Toggle expanded state for a section
  const toggleSectionExpand = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  
  // Helper function to limit items displayed
  const limitItems = (items, section) => {
    if (expandedSections[section] || items.length <= 5) {
      return items;
    }
    return items.slice(0, 5);
  };
  
  // Fetch assigned projects
  useEffect(() => {
    const fetchAssignedProjects = async () => {
      if (!currentUser?.id) return;
      
      try {
        // Get all projects first
        const projectsResponse = await api.get('/projects');
        const allProjects = projectsResponse.data.projects || [];
        
        // Get assignment data for the current user
        const userAssignmentsResponse = await api.get(`/assignments/user/${currentUser.id}`);
        const userAssignments = userAssignmentsResponse.data.assignments || [];
        
        // Filter to projects where user has an assignment
        const userProjectIds = [...new Set(userAssignments
          .filter(a => a.entity_type === 'project' || a.entity_type === 'phase' || a.entity_type === 'step')
          .map(a => {
            if (a.entity_type === 'project') return a.entity_id;
            // For phase and step assignments, we need to find the project ID
            if (a.entity_type === 'phase') {
              const phase = allProjects.flatMap(p => p.phases).find(phase => phase.id === a.entity_id);
              return phase?.project_id;
            }
            if (a.entity_type === 'step') {
              for (const project of allProjects) {
                for (const phase of project.phases || []) {
                  if (phase.steps.some(step => step.id === a.entity_id)) {
                    return project.id;
                  }
                }
              }
            }
            return null;
          })
          .filter(Boolean))];
        
        // Get projects with assignments, excluding completed and canceled projects
        const filteredProjects = allProjects.filter(p => 
          userProjectIds.includes(p.id) && 
          p.status !== 'Completed' && 
          p.status !== 'Canceled'
        );
        
        // Sort by due date (ascending) and limit to 5 projects
        const sortedProjects = filteredProjects
          .sort((a, b) => {
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
          })
          .slice(0, 5);
        
        setAssignedProjects(sortedProjects);
      } catch (err) {
        console.error('Error fetching assigned projects:', err);
        setError('Failed to load assigned projects');
      }
    };
    
    fetchAssignedProjects();
  }, [currentUser]);
  
  // Fetch recent assignments
  useEffect(() => {
    const fetchRecentAssignments = async () => {
      if (!currentUser?.id) return;
      
      try {
        const hours = assignmentsTimeFilter === '72h' ? 72 : 168;
        
        // This is a placeholder - you may need to create this API endpoint
        const response = await api.get(`/assignments/recent?hours=${hours}&userId=${currentUser.id}`);
        const assignments = response.data.assignments || [];
        
        // Enhance assignments with entity details
        const enhancedAssignments = [];
        
        for (const assignment of assignments) {
          try {
            let entityDetails = null;
            
            // Get entity details based on type
            if (assignment.entity_type === 'project') {
              const projectResponse = await api.get(`/projects/${assignment.entity_id}`);
              entityDetails = {
                name: projectResponse.data.project.name,
                projectName: projectResponse.data.project.name,
                projectId: projectResponse.data.project.id
              };
            } else if (assignment.entity_type === 'phase') {
              const phaseResponse = await api.get(`/phases/${assignment.entity_id}`);
              entityDetails = {
                name: phaseResponse.data.phase.name,
                projectId: phaseResponse.data.phase.project_id,
                projectName: phaseResponse.data.phase.project_name || 'Unknown Project'
              };
            } else if (assignment.entity_type === 'step') {
              const stepResponse = await api.get(`/steps/${assignment.entity_id}`);
              entityDetails = {
                name: stepResponse.data.step.name,
                projectId: stepResponse.data.step.project_id,
                projectName: stepResponse.data.step.phase_name 
                  ? `${stepResponse.data.step.phase_name} / Project`
                  : 'Unknown Project'
              };
            }
            
            if (entityDetails) {
              enhancedAssignments.push({
                ...assignment,
                entityName: entityDetails.name,
                projectName: entityDetails.projectName,
                projectId: entityDetails.projectId
              });
            }
          } catch (entityError) {
            console.error(`Error fetching details for ${assignment.entity_type} ${assignment.entity_id}:`, entityError);
          }
        }
        
        setRecentAssignments(enhancedAssignments);
      } catch (err) {
        console.error('Error fetching recent assignments:', err);
        setError('Failed to load recent assignments');
      }
    };
    
    fetchRecentAssignments();
  }, [currentUser, assignmentsTimeFilter]);
  
  // Fetch pending missing items
  useEffect(() => {
    const fetchPendingMissingItems = async () => {
      if (!currentUser?.id) return;
      
      try {
        const response = await api.get('/missing-items/pending');
        setPendingMissingItems(response.data.missingItems || []);
      } catch (err) {
        console.error('Error fetching pending missing items:', err);
        setError('Failed to load missing items');
      }
    };
    
    fetchPendingMissingItems();
  }, [currentUser]);
  
  // Fetch recently received items
  useEffect(() => {
    const fetchRecentlyReceivedItems = async () => {
      if (!currentUser?.id) return;
      
      try {
        const hours = receivedItemsTimeFilter === '72h' ? 72 : 168;
        
        const response = await api.get('/missing-items/recently-received', {
          params: { hours }
        });
        
        setRecentlyReceivedItems(response.data.receivedItems || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching recently received items:', err);
        setError('Failed to load received items');
        setLoading(false);
      }
    };
    
    fetchRecentlyReceivedItems();
  }, [currentUser, receivedItemsTimeFilter]);
  
  // Fetch recent notes
  useEffect(() => {
    const fetchRecentNotes = async () => {
      if (!currentUser?.id) return;
      
      try {
        const hours = noteTimeFilter === '72h' ? 72 : 168;
        
        const response = await api.get('/missing-items/recent-notes', {
          params: { hours }
        });
        
        setRecentNotes(response.data.notes || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching recent notes:', err);
        setError('Failed to load notes');
        setLoading(false);
      }
    };
    
    fetchRecentNotes();
  }, [currentUser, noteTimeFilter]);
  
  // Fetch client files
  useEffect(() => {
    const fetchClientFiles = async () => {
      if (!currentUser?.id) return;
      
      try {
        const hours = clientFilesTimeFilter === '72h' ? 72 : 168;
        
        // Get all projects first (for filtering assigned projects)
        const projectsResponse = await api.get('/projects');
        const allProjects = projectsResponse.data.projects || [];
        
        // Get assignment data for the current user
        const userAssignmentsResponse = await api.get(`/assignments/user/${currentUser.id}`);
        const userAssignments = userAssignmentsResponse.data.assignments || [];
        
        // Get projects assigned to the user
        const userProjectIds = [...new Set(userAssignments
          .filter(a => a.entity_type === 'project' || a.entity_type === 'phase' || a.entity_type === 'step')
          .map(a => {
            if (a.entity_type === 'project') return a.entity_id;
            // For phase and step assignments, we need to find the project ID
            if (a.entity_type === 'phase') {
              const phase = allProjects.flatMap(p => p.phases).find(phase => phase.id === a.entity_id);
              return phase?.project_id;
            }
            if (a.entity_type === 'step') {
              for (const project of allProjects) {
                for (const phase of project.phases || []) {
                  if (phase.steps.some(step => step.id === a.entity_id)) {
                    return project.id;
                  }
                }
              }
            }
            return null;
          })
          .filter(Boolean))];
          
        // Get the active projects (not completed or canceled)
        const activeAssignedProjects = allProjects.filter(p => 
          userProjectIds.includes(p.id) && 
          p.status !== 'Completed' && 
          p.status !== 'Canceled'
        );
        
        // Get the project IDs for filtering files
        const activeProjectIds = activeAssignedProjects.map(p => p.id);
        
        // Get client uploaded files from the last 72 or 168 hours
        const response = await api.get('/files/client-files', {
          params: { hours, projectIds: activeProjectIds.join(',') }
        });
        
        setClientFiles(response.data.files || []);
      } catch (err) {
        console.error('Error fetching client files:', err);
        setError('Failed to load client files');
      }
    };
    
    fetchClientFiles();
  }, [currentUser, clientFilesTimeFilter]);
  
  // Fetch upcoming and overdue tasks
  useEffect(() => {
    const fetchTasks = async () => {
      if (!currentUser?.id) return;
      
      try {
        // Get tasks assigned to the current user that are not completed
        const response = await api.get('/tasks', {
          params: { 
            assigned_user_id: currentUser.id,
            is_completed: 'false'
          }
        });
        
        const userTasks = response.data.tasks || [];
        setTasks(userTasks);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks');
      }
    };
    
    fetchTasks();
  }, [currentUser]);
  
  // Handle opening a note in a modal
  const handleOpenNote = (note) => {
    setSelectedNote(note);
    setShowNoteModal(true);
  };
  
  // Handle closing the note modal
  const handleCloseNote = () => {
    setShowNoteModal(false);
    setSelectedNote(null);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-sm text-red-800 underline"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <span className="bg-blue-100 text-blue-700 p-2 rounded-lg mr-3">
              <ClipboardDocumentListIcon className="h-8 w-8" />
            </span>
            My Dashboard
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Your work overview and alerts
          </p>
        </div>
      </div>
      
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {/* My Projects Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-lg p-2 mr-3">
                <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">My Projects</h2>
                <p className="text-sm text-black">Projects assigned to you</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {assignedProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No assigned projects</h3>
                <p className="mt-1 text-sm text-gray-500">You don't have any projects assigned to you</p>
              </div>
            ) : (
              <>
                {limitItems(assignedProjects, 'myProjects').map(project => (
                  <div key={project.id} className="p-4 hover:bg-blue-50 transition-colors duration-150">
                    <div className="flex justify-between items-start">
                      <div>
                        <Link 
                          to={`/projects/${project.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 block text-lg mb-1"
                        >
                          {project.name}
                        </Link>
                        {project.due_date && (
                          <p className={`flex items-center ${isOverdue(project.due_date) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {isOverdue(project.due_date) ? (
                              <>
                                <ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-600" />
                                <span className="font-medium">Due: {format(new Date(project.due_date), 'MMM d, yyyy')}</span>
                              </>
                            ) : (
                              <span>Due: {format(new Date(project.due_date), 'MMM d, yyyy')}</span>
                            )}
                          </p>
                        )}
                        {/* Add progress indicator */}
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${Math.floor(Math.random() * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <Link 
                        to={`/projects/${project.id}`}
                        className="flex-shrink-0 bg-blue-50 p-2 rounded-full text-blue-400 hover:text-blue-600 hover:bg-blue-100 transition-colors duration-150"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {assignedProjects.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('myProjects')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.myProjects 
                        ? "Show less" 
                        : `View ${assignedProjects.length - 5} more projects`}
                    </button>
                  </div>
                )}
              </>
            )}
            {assignedProjects.length > 0 && (
              <div className="p-4 text-center">
                <Link 
                  to="/my-work" 
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View all assigned projects
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {/* New Assignments Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-purple-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
            <div className="flex items-center">
              <div className="bg-purple-100 rounded-lg p-2 mr-3">
                <BellAlertIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">New Assignments</h2>
                <p className="text-sm text-black">Recently assigned items</p>
              </div>
            </div>
            <div>
              <select
                id="assignments-time-filter"
                value={assignmentsTimeFilter}
                onChange={(e) => setAssignmentsTimeFilter(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="72h">Last 72 hours</option>
                <option value="168h">Last week</option>
              </select>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentAssignments.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <ClockIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent assignments</h3>
                <p className="mt-1 text-sm text-gray-500">You haven't been assigned any new tasks recently</p>
              </div>
            ) : (
              <>
                {limitItems(recentAssignments, 'recentAssignments').map(assignment => (
                  <div key={assignment.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${getRoleColor(assignment.role_type)}`}>
                            {assignment.role_type} - {getRoleName(assignment.role_type)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDistance(new Date(assignment.created_at), new Date(), { addSuffix: true })}
                          </span>
                        </div>
                        <Link 
                          to={`/${assignment.entity_type}s/${assignment.entity_id}`}
                          className="block mt-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          {assignment.entityName || `${assignment.entity_type} #${assignment.entity_id}`}
                        </Link>
                        {assignment.projectName && (
                          <p className="mt-1 text-xs text-gray-500">
                            Project: {assignment.projectName}
                          </p>
                        )}
                      </div>
                      <Link 
                        to={`/${assignment.entity_type}s/${assignment.entity_id}`}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {recentAssignments.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('recentAssignments')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.recentAssignments 
                        ? "Show less" 
                        : `View ${recentAssignments.length - 5} more assignments`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Missing Information Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-amber-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-white">
            <div className="flex items-center">
              <div className="bg-amber-100 rounded-lg p-2 mr-3">
                <EnvelopeIcon className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Missing Information</h2>
                <p className="text-sm text-black">Items requiring information from clients or team</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingMissingItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <DocumentTextIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No missing items</h3>
                <p className="mt-1 text-sm text-gray-500">There are no pending missing information items</p>
              </div>
            ) : (
              <>
                {limitItems(pendingMissingItems, 'pendingMissingItems').map(item => (
                  <div key={item.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mb-2">
                          Pending
                        </span>
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {item.content}
                        </p>
                        {item.project_name && (
                          <p className="mt-1 text-xs text-gray-500">
                            Project: {item.project_name}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDistance(new Date(item.created_at), new Date(), { addSuffix: true })}
                        </p>
                      </div>
                      <Link 
                        to={`/projects/${item.project_id}?tab=missing-items`}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {pendingMissingItems.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('pendingMissingItems')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.pendingMissingItems 
                        ? "Show less" 
                        : `View ${pendingMissingItems.length - 5} more items`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Recently Received Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-green-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-green-50 to-white">
            <div className="flex items-center">
              <div className="bg-green-100 rounded-lg p-2 mr-3">
                <InboxIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Recently Received</h2>
                <p className="text-sm text-black">Items that were recently marked as received</p>
              </div>
            </div>
            <div>
              <select
                id="received-time-filter"
                value={receivedItemsTimeFilter}
                onChange={(e) => setReceivedItemsTimeFilter(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
              >
                <option value="72h">Last 72 hours</option>
                <option value="168h">Last week</option>
              </select>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentlyReceivedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <InboxIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recently received items</h3>
                <p className="mt-1 text-sm text-gray-500">No items have been marked as received recently</p>
              </div>
            ) : (
              <>
                {limitItems(recentlyReceivedItems, 'recentlyReceivedItems').map(item => (
                  <div key={item.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                          Received
                        </span>
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {item.content}
                        </p>
                        {item.project_name && (
                          <p className="mt-1 text-xs text-gray-500">
                            Project: {item.project_name}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDistance(new Date(item.updated_at), new Date(), { addSuffix: true })}
                        </p>
                      </div>
                      <Link 
                        to={`/projects/${item.project_id}?tab=missing-items`}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {recentlyReceivedItems.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('recentlyReceivedItems')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.recentlyReceivedItems 
                        ? "Show less" 
                        : `View ${recentlyReceivedItems.length - 5} more items`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Recent Notes Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-sky-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-sky-50 to-white">
            <div className="flex items-center">
              <div className="bg-sky-100 rounded-lg p-2 mr-3">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-sky-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Recent Notes</h2>
                <p className="text-sm text-black">Recently added notes</p>
              </div>
            </div>
            <div>
              <select
                id="note-time-filter"
                value={noteTimeFilter}
                onChange={(e) => setNoteTimeFilter(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              >
                <option value="72h">Last 72 hours</option>
                <option value="168h">Last week</option>
              </select>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentNotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent notes</h3>
                <p className="mt-1 text-sm text-gray-500">No notes have been added recently</p>
              </div>
            ) : (
              <>
                {limitItems(recentNotes, 'recentNotes').map(note => (
                  <div key={note.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                          Note
                        </span>
                        <button 
                          onClick={() => handleOpenNote(note)}
                          className="block text-sm font-medium text-blue-600 hover:text-blue-800 w-full text-left"
                        >
                          <div className="text-sm font-medium text-gray-900 line-clamp-1 overflow-hidden rich-text-content">
                            {renderContentWithMentions(note.content)}
                          </div>
                        </button>
                        {note.project_name && (
                          <p className="mt-1 text-xs text-gray-500">
                            Project: {note.project_name}
                          </p>
                        )}
                        {note.created_by_name && (
                          <p className="mt-1 text-xs text-gray-500">
                            By: {note.created_by_name}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDistance(new Date(note.created_at), new Date(), { addSuffix: true })}
                        </p>
                      </div>
                      <button 
                        onClick={() => handleOpenNote(note)}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {recentNotes.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('recentNotes')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.recentNotes 
                        ? "Show less" 
                        : `View ${recentNotes.length - 5} more notes`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Client Files Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-indigo-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
            <div className="flex items-center">
              <div className="bg-indigo-100 rounded-lg p-2 mr-3">
                <DocumentIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Client Files</h2>
                <p className="text-sm text-black">Recently uploaded files from clients</p>
              </div>
            </div>
            <div>
              <select
                id="client-files-time-filter"
                value={clientFilesTimeFilter}
                onChange={(e) => setClientFilesTimeFilter(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="72h">Last 72 hours</option>
                <option value="168h">Last week</option>
              </select>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {clientFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <DocumentIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent files</h3>
                <p className="mt-1 text-sm text-gray-500">No files have been uploaded by clients recently</p>
              </div>
            ) : (
              <>
                {limitItems(clientFiles, 'clientFiles').map(file => (
                  <div key={file.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                          File Upload
                        </span>
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {file.original_filename || file.filename}
                        </p>
                        {file.project_name && (
                          <p className="mt-1 text-xs text-gray-500">
                            Project: {file.project_name}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDistance(new Date(file.created_at), new Date(), { addSuffix: true })}
                        </p>
                      </div>
                      <Link 
                        to={`/projects/${file.project_id}?tab=files`}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {clientFiles.length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('clientFiles')}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {expandedSections.clientFiles 
                        ? "Show less" 
                        : `View ${clientFiles.length - 5} more files`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Overdue Tasks Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-red-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-white">
            <div className="flex items-center">
              <div className="bg-red-100 rounded-lg p-2 mr-3">
                <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Overdue Tasks</h2>
                <p className="text-sm text-black">Tasks past their due date</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {tasks.filter(task => isOverdue(task.due_date)).length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No overdue tasks</h3>
                <p className="mt-1 text-sm text-gray-500">You don't have any tasks past their due date</p>
              </div>
            ) : (
              <>
                {limitItems(
                  tasks
                    .filter(task => isOverdue(task.due_date))
                    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)), 
                  'overdueTasks'
                ).map(task => (
                  <div key={task.id} className="p-5 hover:bg-red-50 transition-colors duration-150 group">
                    <div className="flex justify-between items-start">
                      <div className="w-full">
                        <div className="flex items-center mb-2">
                          <span className="mr-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                            {Math.floor(Math.abs((new Date(task.due_date) - new Date()) / (1000 * 60 * 60 * 24)))} days overdue
                          </span>
                        </div>
                        <Link 
                          to="/tasks"
                          className="text-lg font-medium text-red-600 hover:text-red-800 group-hover:underline block mb-1"
                        >
                          {task.name}
                        </Link>
                        <p className="text-sm text-red-600 font-medium flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Due: {format(new Date(task.due_date), 'MMM d, yyyy')}
                        </p>
                        {task.description && (
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                      </div>
                      <Link 
                        to="/tasks"
                        className="flex-shrink-0 bg-red-50 p-2 rounded-full text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors duration-150 ml-2"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {tasks.filter(task => isOverdue(task.due_date)).length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('overdueTasks')}
                      className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-full text-sm font-medium transition-colors duration-150 inline-flex items-center"
                    >
                      {expandedSections.overdueTasks 
                        ? (
                          <>
                            <span>Show less</span>
                            <ChevronRightIcon className="h-4 w-4 ml-1 transform rotate-90" />
                          </>
                        ) 
                        : (
                          <>
                            <span>View {tasks.filter(task => isOverdue(task.due_date)).length - 5} more tasks</span>
                            <ChevronRightIcon className="h-4 w-4 ml-1 transform -rotate-90" />
                          </>
                        )
                      }
                    </button>
                  </div>
                )}
              </>
            )}
            
            <div className="p-4 text-center">
              <Link 
                to="/tasks?filter=overdue" 
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors duration-150"
              >
                View all overdue tasks
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Upcoming Tasks Section */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-yellow-500 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-white">
            <div className="flex items-center">
              <div className="bg-yellow-100 rounded-lg p-2 mr-3">
                <CalendarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black">Due Soon</h2>
                <p className="text-sm text-black">Tasks due in the next 7 days</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {tasks.filter(task => isDueSoon(task.due_date)).length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming tasks</h3>
                <p className="mt-1 text-sm text-gray-500">You don't have any tasks due in the next 7 days</p>
              </div>
            ) : (
              <>
                {limitItems(
                  tasks
                    .filter(task => isDueSoon(task.due_date))
                    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)),
                  'upcomingTasks'
                ).map(task => (
                  <div key={task.id} className="p-5 hover:bg-yellow-50 transition-colors duration-150 group">
                    <div className="flex justify-between items-start">
                      <div className="w-full">
                        <div className="flex items-center mb-2">
                          <span className="mr-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                            {Math.floor(Math.abs((new Date(task.due_date) - new Date()) / (1000 * 60 * 60 * 24)))} days left
                          </span>
                        </div>
                        <Link 
                          to="/tasks"
                          className="text-lg font-medium text-yellow-700 hover:text-yellow-800 group-hover:underline block mb-1"
                        >
                          {task.name}
                        </Link>
                        <p className="text-sm text-yellow-600 flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Due: {format(new Date(task.due_date), 'MMM d, yyyy')}
                        </p>
                        {task.description && (
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                      </div>
                      <Link 
                        to="/tasks"
                        className="flex-shrink-0 bg-yellow-50 p-2 rounded-full text-yellow-400 hover:text-yellow-600 hover:bg-yellow-100 transition-colors duration-150 ml-2"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {tasks.filter(task => isDueSoon(task.due_date)).length > 5 && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => toggleSectionExpand('upcomingTasks')}
                      className="px-4 py-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-full text-sm font-medium transition-colors duration-150 inline-flex items-center"
                    >
                      {expandedSections.upcomingTasks 
                        ? (
                          <>
                            <span>Show less</span>
                            <ChevronRightIcon className="h-4 w-4 ml-1 transform rotate-90" />
                          </>
                        ) 
                        : (
                          <>
                            <span>View {tasks.filter(task => isDueSoon(task.due_date)).length - 5} more tasks</span>
                            <ChevronRightIcon className="h-4 w-4 ml-1 transform -rotate-90" />
                          </>
                        )
                      }
                    </button>
                  </div>
                )}
              </>
            )}
            
            <div className="p-4 text-center">
              <Link 
                to="/tasks?filter=upcoming" 
                className="inline-flex items-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md text-sm font-medium transition-colors duration-150"
              >
                View all upcoming tasks
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Note Modal */}
      {showNoteModal && selectedNote && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Note Details
                      </h3>
                      <button
                        type="button"
                        className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={handleCloseNote}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    
                    <div className="mt-4">
                      <div className="bg-gray-50 p-4 rounded-lg mt-2">
                        <div className="text-sm text-gray-700 rich-text-content">
                          {renderContentWithMentions(selectedNote.content)}
                        </div>
                      </div>
                      
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        {selectedNote.project_name && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</h4>
                            <p className="mt-1 text-sm text-gray-900">{selectedNote.project_name}</p>
                          </div>
                        )}
                        
                        {selectedNote.created_by_name && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Created By</h4>
                            <p className="mt-1 text-sm text-gray-900">{selectedNote.created_by_name}</p>
                          </div>
                        )}
                        
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {format(new Date(selectedNote.created_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                        
                        {selectedNote.status && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</h4>
                            <p className="mt-1 text-sm text-gray-900">{selectedNote.status}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCloseNote}
                >
                  Close
                </button>
                {selectedNote.project_id && (
                  <Link
                    to={`/projects/${selectedNote.project_id}`}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Go to Project
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDashboard; 